import { Button, Col, Row, Spin, Switch } from 'antd';
import { FieldKeys } from '../../../common/api/services/HistoryApi/types/fieldKeys';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { historyApi } from '../../../common/api/services/HistoryApi/historyApi';
import { useParams } from 'react-router-dom';
import {
    addNewActiveField,
    moveToActive,
    moveToHistory,
    removeFromActiveField,
    removeFromHistoryField,
    setHistoryFields,
} from '../../../redux/reducers/onboarding-history/sliceReducer';
import { useEffect, useState } from 'react';
import { ShownTypes } from '../../../redux/reducers/onboarding-history/types';
import HistoryField from './components/HistoryField';
import TrashIcon from '../../../assets/icons/TrashIcon';
import DeleteHistoryModal from './components/DeleteHistoryModal';
import { getError } from '../../../common/api/utils/getError';
import CrossIcon from '../../../assets/icons/CrossIcon';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import { openErrorNotification, openSuccessNotification } from '../../../components/notification/Notification';
import { IHistoryProps } from '../../../common/api/services/HistoryApi/types/historyTypes';
import {
    addNewActiveFieldCompany,
    moveToActiveCompany,
    moveToHistoryCompany,
    removeFromActiveFieldCompany,
    removeFromHistoryFieldCompany,
    setHistoryFieldsCompany,
} from '../../../redux/reducers/onboarding-history-WC/sliceReducer';

interface IProps {
    fieldKey: FieldKeys;
    tabName: string;
    shownElements: IHistoryProps;
    onChangeArray: Array<{ key: string; onChange: () => void }>;
    // TODO: wrong order of labels for the old values
    labels?: Array<{ [key: string]: string }>;
    //NOTE: required for WC and CC
    companyId?: string;
    isHideTabName?: boolean;
    disableCondition?: boolean;
    isView?: boolean;
}

const HistoryComponent = (props: IProps) => {
    const { fieldKey, shownElements, tabName, labels = [], companyId, isHideTabName, disableCondition, isView } = props;
    const dispatch = useAppDispatch();
    const { consultantId } = useParams<{
        consultantId: string;
    }>();
    const isWorkContractField = fieldKey === FieldKeys.invoiceCurrency || fieldKey === FieldKeys.workTimeContract;

    const totalHistory = useAppSelector((state) => state.onBoardingHistory.total as Partial<Record<FieldKeys, number>>);

    const totalWC = useAppSelector((state) => state.onBoardingHistoryWC.total as Partial<Record<FieldKeys, number>>);
    const total: any = isWorkContractField ? totalWC : totalHistory;

    const shownTotal = isWorkContractField
        ? total[fieldKey]?.find((item: any) => item.companyId === companyId)?.count || 0
        : total[fieldKey];

    const historyOnboarding = useAppSelector((state) => state.onBoardingHistory.historyFields);

    const historyWC = useAppSelector((state) => state.onBoardingHistoryWC.historyFields);

    const history = isWorkContractField ? historyWC : historyOnboarding;

    const activeFieldsOnboarding = useAppSelector((state) => state.onBoardingHistory.activeFields);

    const activeFieldsWC = useAppSelector((state) => state.onBoardingHistoryWC.activeFields);

    const activeFields = isWorkContractField ? activeFieldsWC : activeFieldsOnboarding;

    const activeFieldsItems = (activeFields[fieldKey] || [])
        .map((item) => {
            const lebelsArrayToObj = labels.reduce((acc, item) => {
                acc[item.key] = item.value;
                return acc;
            }, {});
            return {
                ...item,
                ...lebelsArrayToObj,
            };
        })
        //NOTE: for CC
        .filter((item) => (companyId ? item?.companyId === companyId : item));
    const historyFieldItems = (history[fieldKey] || [])
        .map((item) => {
            const lebelsArrayToObj = labels.reduce((acc, item) => {
                acc[item.key] = item.value;
                return acc;
            }, {});
            return {
                ...item,
                ...lebelsArrayToObj,
            };
        })
        //NOTE: for CC
        .filter((item) => (companyId ? item?.companyId === companyId : item));

    const errorMessages = useAppSelector((state) => state.onBoardingHistory.errorMessages);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpenHistory, setIsOpenHistory] = useState(false);
    const [isFirstOpenHistory, setIsFirstOpenHistory] = useState(true);
    const [selectedDelete, setSelectedDelete] = useState<{
        fieldKey: string;
        index: number;
        newField: Record<string, unknown>;
        isActive: boolean;
    } | null>(null);

    const newField = shownElements.reduce((acc, element) => {
        if (['isReligious', 'bankAccount'].includes(element.key)) {
            acc[element.key] = null;
        } else {
            acc[element.key] =
                element.inputType === 'number'
                    ? 0
                    : element.type === ShownTypes.uploadFile
                    ? []
                    : element.type === ShownTypes.checkbox ||
                      element.type === ShownTypes.switch ||
                      element.type === ShownTypes.radio
                    ? false
                    : '';
        }

        return acc;
    }, {} as Record<string, string | boolean | undefined | Array<any> | null | number>);

    useEffect(() => {
        const errorMessage = getError(`histories.${fieldKey}`, errorMessages);
        if (errorMessage) {
            openErrorNotification(errorMessage);
        }
    }, [errorMessages]);

    const isHistoryError = errorMessages
        .map((item) => {
            const match = item.property.match(/\[(\d+)]/);
            if (match) {
                const index = parseInt(match[1], 10);
                if (activeFieldsItems?.length && index >= activeFieldsItems?.length) {
                    return item;
                }
            }
            // return item;
        })
        .find((item) => {
            return item?.property?.includes(fieldKey);
        });

    const getHistory = () => {
        setIsLoading(true);
        historyApi
            .getOnboardingHistoryFields({
                userId: consultantId || 'consultant',
                keys: [fieldKey],
            })
            .then((resp: any) => {
                dispatch(
                    isWorkContractField
                        ? setHistoryFieldsCompany({ [fieldKey]: resp.data[fieldKey] })
                        : setHistoryFields({ [fieldKey]: resp.data[fieldKey] })
                );
            })
            .finally(() => setIsLoading(false));
    };

    const onOpenHistory = () => {
        if (!isOpenHistory) {
            setIsOpenHistory(true);
            if (isFirstOpenHistory) {
                getHistory();
                setIsFirstOpenHistory(false);
            }
        } else {
            setIsOpenHistory(false);
        }
    };

    const onAddNew = () => {
        if ('hasWorkPermit' in newField) {
            newField.hasWorkPermit = true;
        }
        const fieldToAdd = companyId ? { ...newField, companyId } : newField;
        if (activeFieldsItems.length === 2) {
            openErrorNotification(`You can only have two visible ${tabName}. Please deactivate one to continue.`);
            return;
        }

        dispatch(
            isWorkContractField
                ? addNewActiveFieldCompany({
                      fieldKey,
                      newField: fieldToAdd,
                  })
                : addNewActiveField({
                      fieldKey,
                      newField: fieldToAdd,
                  })
        );
        openSuccessNotification('New field was added successfully');
    };

    const onDeleteField = () => {
        if (selectedDelete) {
            //active field
            if (selectedDelete.isActive) {
                //for id first delete from DB
                if (activeFieldsItems[selectedDelete.index]?._id) {
                    historyApi
                        .deleteOnboardingHistoryFields({
                            userId: consultantId || 'consultant',
                            historyName: selectedDelete.fieldKey,
                            id: activeFieldsItems[selectedDelete.index]?._id,
                        })
                        .then(() => {
                            dispatch(
                                isWorkContractField
                                    ? removeFromActiveFieldCompany({
                                          fieldKey: selectedDelete.fieldKey,
                                          index: selectedDelete.index,
                                          newField: companyId ? { ...newField, companyId } : newField,
                                      })
                                    : removeFromActiveField({
                                          fieldKey: selectedDelete.fieldKey,
                                          index: selectedDelete.index,
                                          newField,
                                      })
                            );
                            setSelectedDelete(null);
                        });
                } else {
                    //if no id just delete
                    dispatch(
                        isWorkContractField
                            ? removeFromActiveFieldCompany({
                                  fieldKey: selectedDelete.fieldKey,
                                  index: selectedDelete.index,
                                  newField: companyId ? { ...newField, companyId } : newField,
                              })
                            : removeFromActiveField({
                                  fieldKey: selectedDelete.fieldKey,
                                  index: selectedDelete.index,
                                  newField,
                              })
                    );
                    setSelectedDelete(null);
                }
            } else {
                //history field
                //for id first delete from DB
                if (historyFieldItems[selectedDelete.index]?._id) {
                    historyApi
                        .deleteOnboardingHistoryFields({
                            userId: consultantId || 'consultant',
                            historyName: selectedDelete.fieldKey,
                            id: historyFieldItems[selectedDelete.index]?._id,
                        })
                        .then(() => {
                            dispatch(
                                isWorkContractField
                                    ? removeFromHistoryFieldCompany({
                                          fieldKey: selectedDelete.fieldKey,
                                          index: selectedDelete.index,
                                          companyId,
                                      })
                                    : removeFromHistoryField({
                                          fieldKey: selectedDelete.fieldKey,
                                          index: selectedDelete.index,
                                      })
                            );
                            setSelectedDelete(null);
                        });
                } else {
                    //if no id just delete
                    dispatch(
                        isWorkContractField
                            ? removeFromHistoryFieldCompany({
                                  fieldKey: selectedDelete.fieldKey,
                                  index: selectedDelete.index,
                                  companyId,
                              })
                            : removeFromHistoryField({
                                  fieldKey: selectedDelete.fieldKey,
                                  index: selectedDelete.index,
                              })
                    );
                    setSelectedDelete(null);
                }
            }
        }
    };

    const errorPath = getError(`histories.${fieldKey}`, errorMessages);

    return (
        <Row className="onboarding-history-container">
            <Spin spinning={isLoading}>
                {!isHideTabName && <h5>{tabName}</h5>}
                {errorPath && (
                    <span className="error-message error-text">
                        <div className="icon-wrapper">
                            <CrossIcon />
                        </div>
                        {errorPath}
                    </span>
                )}
                <Button onClick={onAddNew} className="add-new-history-btn" disabled={!!disableCondition || isView}>
                    +Add a new {tabName.toLowerCase()}
                </Button>
                {activeFieldsItems?.map((field, index) => {
                    const filteredKeys = shownElements
                        .filter((field) => {
                            if (field.condition) {
                                return field.condition(activeFieldsItems?.[index] || {});
                            }
                            return true;
                        })
                        .map((el) => el.key);
                    const groupedKeys = [];
                    let currentGroup = [];
                    let currentSpanSum = 0;

                    for (let i = 0; i < filteredKeys.length; i++) {
                        const key = filteredKeys[i];
                        const fieldConfig = shownElements.find((el) => el.key === key);
                        const span = fieldConfig?.span || 12;
                        if (currentSpanSum + span > 24) {
                            groupedKeys.push(currentGroup);
                            currentGroup = [];
                            currentSpanSum = 0;
                        }

                        currentGroup.push(key);
                        currentSpanSum += span;
                    }
                    if (currentGroup.length > 0) {
                        groupedKeys.push(currentGroup);
                    }

                    return (
                        <div key={`active-${index}`} className="onboarding-history-block">
                            <div className="onboarding-status-wrapper">
                                <div className="onboarding-status-container">
                                    <span
                                        style={{
                                            fontWeight: 'bold',
                                            borderRight: '2px solid #E6ECF2',
                                            paddingRight: '15px',
                                        }}
                                    >
                                        {tabName}
                                    </span>
                                    <div className="onboarding-switch-container">
                                        <span>Active</span>
                                        <Switch
                                            onChange={() =>
                                                dispatch(
                                                    isWorkContractField
                                                        ? moveToHistoryCompany({
                                                              fieldKey,
                                                              index,
                                                              newField: companyId
                                                                  ? { ...newField, companyId }
                                                                  : newField,
                                                          })
                                                        : moveToHistory({ fieldKey, index, newField })
                                                )
                                            }
                                            checked={true}
                                            disabled={!!disableCondition || isView}
                                        />
                                    </div>
                                </div>
                                <TrashIcon
                                    onClick={() => {
                                        if (!!disableCondition || isView) {
                                            return;
                                        }
                                        setSelectedDelete({
                                            fieldKey,
                                            index,
                                            newField,
                                            isActive: true,
                                        });
                                    }}
                                />
                            </div>
                            {groupedKeys.map((group, groupIndex) => (
                                <Row key={`group-${groupIndex}`} gutter={[16, 16]}>
                                    {group.map((item) => {
                                        const fieldConfig = shownElements.find((value) => value.key === item);
                                        return (
                                            <Col span={fieldConfig?.span || 12} key={item} style={{ marginTop: '5px' }}>
                                                <HistoryField
                                                    fieldKey={fieldKey}
                                                    isActiveField={true}
                                                    value={field[item] as any}
                                                    field={fieldConfig}
                                                    index={index}
                                                    companyId={companyId}
                                                    isView={isView}
                                                />
                                            </Col>
                                        );
                                    })}
                                </Row>
                            ))}
                        </div>
                    );
                })}

                <Button
                    onClick={onOpenHistory}
                    className={`open-history-btn ${shownTotal && shownTotal! > 0 ? 'active' : ''} ${
                        isHistoryError ? 'error' : ''
                    }`}
                >
                    {tabName} history ({shownTotal || 0}){' '}
                    {shownTotal && shownTotal! > 0 ? isOpenHistory ? <CaretUpOutlined /> : <CaretDownOutlined /> : null}
                </Button>
                {isOpenHistory &&
                    historyFieldItems?.map((field, index) => {
                        const filteredKeys = shownElements
                            .filter((field) => {
                                if (field.condition) {
                                    return field.condition(historyFieldItems?.[index] || {});
                                }
                                return true;
                            })
                            .map((el) => el.key);
                        const groupedKeys = [];
                        let currentGroup = [];
                        let currentSpanSum = 0;

                        for (let i = 0; i < filteredKeys.length; i++) {
                            const key = filteredKeys[i];
                            const fieldConfig = shownElements.find((el) => el.key === key);
                            const span = fieldConfig?.span || 12;
                            if (currentSpanSum + span > 24) {
                                groupedKeys.push(currentGroup);
                                currentGroup = [];
                                currentSpanSum = 0;
                            }

                            currentGroup.push(key);
                            currentSpanSum += span;
                        }
                        if (currentGroup.length > 0) {
                            groupedKeys.push(currentGroup);
                        }
                        return (
                            <div key={`active-${index}`} className="onboarding-history-block">
                                <div className="onboarding-status-wrapper">
                                    <div className="onboarding-status-container">
                                        <span style={{ fontWeight: 'bold' }}>{tabName}</span>
                                        <div className="onboarding-switch-container">
                                            <span>Active</span>
                                            <Switch
                                                onChange={() => {
                                                    if (activeFieldsItems.length === 2) {
                                                        openErrorNotification(
                                                            `You can only have two visible ${tabName}. Please deactivate one to continue.`
                                                        );
                                                        return;
                                                    }
                                                    dispatch(
                                                        isWorkContractField
                                                            ? moveToActiveCompany({
                                                                  fieldKey,
                                                                  index,
                                                                  companyId,
                                                              })
                                                            : moveToActive({ fieldKey, index })
                                                    );
                                                }}
                                                checked={false}
                                                disabled={!!disableCondition || isView}
                                            />
                                        </div>
                                    </div>
                                    <TrashIcon
                                        onClick={() => {
                                            if (!!disableCondition || isView) {
                                                return;
                                            }
                                            setSelectedDelete({
                                                fieldKey,
                                                index,
                                                newField,
                                                isActive: false,
                                            });
                                        }}
                                    />
                                </div>

                                {groupedKeys.map((group, groupIndex) => (
                                    <Row key={`group-${groupIndex}`} gutter={[16, 16]}>
                                        {group.map((item) => {
                                            const fieldConfig = shownElements.find((value) => value.key === item);
                                            return (
                                                <Col
                                                    span={fieldConfig?.span || 12}
                                                    key={item}
                                                    style={{ marginTop: '5px' }}
                                                >
                                                    <HistoryField
                                                        fieldKey={fieldKey}
                                                        isActiveField={false}
                                                        value={field[item] as any}
                                                        field={fieldConfig}
                                                        index={index}
                                                        companyId={companyId}
                                                        isView={isView}
                                                    />
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                ))}
                            </div>
                        );
                    })}
            </Spin>
            {selectedDelete && (
                <DeleteHistoryModal
                    isOpen={!!selectedDelete}
                    onClose={() => setSelectedDelete(null)}
                    onSubmit={onDeleteField}
                    tabName={tabName}
                    selectedDelete={selectedDelete}
                    activeFieldsItems={activeFieldsItems}
                    historyFieldItems={historyFieldItems}
                />
            )}
        </Row>
    );
};

export default HistoryComponent;
